/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { ShoppingCart, Star } from 'lucide-react';
import { Product } from '../../../types/products';
import { Button } from '../../ui/Button';

interface ProductCardProps {
    product: Product;
    onAdd: (product: Product) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, onAdd }) => {
    return (
        <div className="bg-white dark:bg-[#15171C] rounded-2xl overflow-hidden border border-gray-100 dark:border-white/5 hover:shadow-xl transition-all duration-300 group flex flex-col h-full">
            <div className="relative aspect-square bg-gray-50 dark:bg-[#0A0B0F] p-6 flex items-center justify-center overflow-hidden">
                <img 
                    src={product.image} 
                    alt={product.name} 
                    className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110" 
                />
                {product.stock && product.stock < 10 && (
                    <div className="absolute top-3 right-3 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide">
                        Low Stock
                    </div>
                )}
            </div>
            
            <div className="p-5 flex flex-col flex-1">
                <div className="flex justify-between items-start mb-2">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{product.category}</div>
                    <div className="flex items-center gap-1 text-yellow-500">
                        <Star size={12} fill="currentColor" />
                        <span className="text-xs font-bold text-gray-500">4.9</span>
                    </div>
                </div>
                
                <h3 className="font-serif text-lg font-bold text-[#0A0B0F] dark:text-white mb-2 leading-tight group-hover:text-nobel-gold transition-colors">{product.name}</h3>
                
                <p className="text-sm text-gray-500 mb-4 line-clamp-2 flex-grow">{product.description}</p>
                
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 dark:border-white/5">
                    <div>
                        <span className="text-xs text-gray-400 block">Price</span>
                        <span className="text-xl font-bold text-[#0A0B0F] dark:text-white">${product.price.toFixed(2)}</span>
                    </div>
                    <Button onClick={() => onAdd(product)} variant="secondary" size="sm" className="shadow-lg hover:shadow-xl">
                        <ShoppingCart size={16} className="mr-2"/> Add
                    </Button>
                </div>
            </div>
        </div>
    );
};